// frontend/src/pages/crm/AgencySettings.js
import React, { useState, useEffect, useRef } from 'react';
import { useCRM } from '../../contexts/CRMContext';
import { useBranding } from '../../contexts/BrandingContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import ColorPicker from '../../components/ui/color-picker';
import ImageUpload from '../../components/ui/image-upload';
import { 
  PlusIcon, 
  BuildingOfficeIcon, 
  PencilIcon, 
  TrashIcon,
  StarIcon,
  LockClosedIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  SwatchIcon, 
  UserIcon, 
  PhotoIcon,
  CheckIcon,
  ArrowPathIcon,
  EyeIcon,
  GlobeAltIcon,
  LinkIcon,
  InformationCircleIcon,
  XCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon
} from '@heroicons/react/24/outline';
import { useDialog, ConfirmationDialog } from '../../components/kibo-ui/dialog';
import AgencyLogo from '../../components/ui/AgencyLogo';
import TeamManagement from './TeamManagement';
import AgencyBilling from './AgencyBilling';
import { agencyAPI } from '../../utils/crmApi';
import crmApi from '../../utils/crmApi';
import toast from 'react-hot-toast';

const AgencySettings = () => {
  const { 
    agencies, 
    currentAgency, 
    createAgency, 
    updateAgency, 
    deleteAgency, 
    switchAgency,
    canCreateAgency,
    getRemainingAgencySlots,
    user
  } = useCRM();
  const { 
    branding, 
    updateBranding, 
    updateColor, 
    updateImage, 
    resetBranding, 
    saveBranding,
    isLoading: brandingLoading 
  } = useBranding();
  const { hasFeature, subscription, getUpgradeSuggestions } = useSubscription();
  const { openDialog } = useDialog();
  
  const [activeTab, setActiveTab] = useState('current-agency');
  const [currentLogoError, setCurrentLogoError] = useState(false);
  const [billingSummary, setBillingSummary] = useState(null);
  const [billingLoading, setBillingLoading] = useState(false);

  // Reset logo error when the current agency logo changes
  useEffect(() => {
    setCurrentLogoError(false);
  }, [currentAgency?.logoUrl]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingAgency, setEditingAgency] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [localBranding, setLocalBranding] = useState(branding);
  
  // Domain management state
  const [domainData, setDomainData] = useState({
    subdomain: '',
    customDomain: ''
  });
  const [domainLoading, setDomainLoading] = useState(false);
  const [verifyingDomain, setVerifyingDomain] = useState(false);
  const [showUnlinkConfirm, setShowUnlinkConfirm] = useState(false);
  const [expandedSteps, setExpandedSteps] = useState({
    step1: true,
    step2: true,
    step3: true,
    step4: false
  });

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    motto: '',
    email: '',
    phone: '',
    address: '',
    website: '',
    logoUrl: ''
  });

  // Update local branding when context changes
  useEffect(() => {
    setLocalBranding(branding);
  }, [branding]);

  // Initialize domain data from current agency (only when agency changes or domains update)
  const prevAgencyIdRef = useRef(null);
  const prevDomainsRef = useRef(null);
  useEffect(() => {
    const currentAgencyId = currentAgency?._id?.toString();
    // Normalize domains data for comparison (handle undefined/missing fields)
    const normalizedDomains = {
      subdomain: currentAgency?.domains?.subdomain || '',
      customDomain: currentAgency?.domains?.customDomain || ''
    };
    const currentDomains = JSON.stringify(normalizedDomains);
    
    // Initialize if agency changed OR if domains data changed
    if (currentAgencyId && (
      currentAgencyId !== prevAgencyIdRef.current ||
      currentDomains !== prevDomainsRef.current
    )) {
      prevAgencyIdRef.current = currentAgencyId;
      prevDomainsRef.current = currentDomains;
      
      // Always update domainData from normalized values
      setDomainData(normalizedDomains);
    }
  }, [currentAgency?._id, currentAgency?.domains?.subdomain, currentAgency?.domains?.customDomain]);

  // Load billing summary for header widgets
  useEffect(() => {
    const loadBilling = async () => {
      if (!currentAgency?._id) return;
      try {
        setBillingLoading(true);
        const res = await crmApi.get(`/agencies/${currentAgency._id}/billing`);
        if (res.data?.success) setBillingSummary(res.data.data);
      } catch (e) {
        // soft-fail
      } finally {
        setBillingLoading(false);
      }
    };
    loadBilling();
  }, [currentAgency?._id]);

  const openPortal = async () => {
    if (!currentAgency?._id) return;
    try {
      const res = await crmApi.post(`/agencies/${currentAgency._id}/billing/portal`, {});
      if (res.data?.success && res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to open billing portal');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleBrandingChange = (field, value) => {
    setLocalBranding(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleColorChange = (colorType, color) => {
    handleBrandingChange(colorType, color);
  };

  const handleImageChange = (imageType, image) => {
    handleBrandingChange(imageType, image);
  };

  const handleCreateAgency = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const result = await createAgency(formData);
      if (result.success) {
        toast.success('Agency created successfully!');
        setShowCreateForm(false);
        setFormData({
          name: '',
          description: '',
          motto: '',
          email: '',
          phone: '',
          address: '',
          website: '',
          logoUrl: ''
        });
      } else {
        toast.error(result.error || 'Failed to create agency');
      }
    } catch (error) {
      toast.error('An error occurred while creating the agency');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateAgency = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const result = await updateAgency(editingAgency._id, formData);
      if (result.success) {
        toast.success('Agency updated successfully!');
        setEditingAgency(null);
        setFormData({
          name: '',
          description: '',
          motto: '',
          email: '',
          phone: '',
          address: '',
          website: '',
          logoUrl: ''
        });
      } else {
        toast.error(result.error || 'Failed to update agency');
      }
    } catch (error) {
      toast.error('An error occurred while updating the agency');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAgency = (agency) => {
    openDialog({
      type: 'confirm',
      variant: 'danger',
      title: 'Delete Agency',
      description: `Are you sure you want to delete "${agency.name}"? This action cannot be undone and will affect all data associated with this agency.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      onConfirm: async () => {
        try {
          const result = await deleteAgency(agency._id);
          if (result.success) {
            toast.success('Agency deleted successfully!');
          } else {
            toast.error(result.error || 'Failed to delete agency');
          }
        } catch (error) {
          toast.error('An error occurred while deleting the agency');
        }
      }
    });
  };

  const handleEditAgency = (agency) => {
    setEditingAgency(agency);
    setFormData({
      name: agency.name || '',
      description: agency.description || '',
      motto: agency.motto || '',
      email: agency.email || '',
      phone: agency.phone || '',
      address: agency.address || '',
      website: agency.website || '',
      logoUrl: agency.logoUrl || ''
    });
  };

  const handleSwitchAgency = async (agencyId) => {
    try {
      await switchAgency(agencyId);
      toast.success('Switched agency successfully!');
    } catch (error) {
      toast.error('Failed to switch agency');
    }
  };

  const handleSaveBranding = async () => {
    try {
      const result = await saveBranding(localBranding);
      if (result.success) {
        setHasChanges(false);
        toast.success('Branding settings saved successfully!');
      } else {
        toast.error('Failed to save branding settings');
      }
    } catch (error) {
      toast.error('An error occurred while saving');
    }
  };

  const handleResetBranding = () => {
    openDialog({
      title: 'Reset Branding',
      description: 'Are you sure you want to reset all branding settings to default? This action cannot be undone.',
      confirmText: 'Reset Branding',
      cancelText: 'Cancel',
      variant: 'danger',
      onConfirm: () => {
        resetBranding();
        setLocalBranding({
          primaryColor: '#3B82F6',
          secondaryColor: '#10B981',
          accentColor: '#F59E0B',
          textColor: '#1F2937',
          backgroundColor: '#FFFFFF',
          userProfileImage: null,
          agencyCoverImage: null,
          agencyName: '',
          agencyMotto: '',
          agencyDescription: '',
          agencyEmail: '',
          agencyPhone: '',
          agencyAddress: '',
          agencyWebsite: '',
          agencyLogo: null,
        });
        setHasChanges(false);
        toast.success('Branding settings reset to default');
      }
    });
  };

  const remainingSlots = getRemainingAgencySlots();
  const canCreate = canCreateAgency();

  // Check if custom domain feature is available (Professional and Enterprise tiers)
  // Uses the whiteLabel feature flag from subscription plans configuration
  const isCustomDomainAvailable = () => {
    return hasFeature('whiteLabel');
  };

  // Handle domain updates
  const handleDomainUpdate = async () => {
    if (!currentAgency) return;
    
    setDomainLoading(true);
    try {
      // Allow empty strings to clear domains
      const domainsUpdate = {};
      
      if (domainData.subdomain !== undefined) {
        const subdomainValue = domainData.subdomain.trim();
        domainsUpdate.subdomain = subdomainValue || undefined;
      }
      
      if (domainData.customDomain !== undefined) {
        const customDomainValue = domainData.customDomain.trim();
        domainsUpdate.customDomain = customDomainValue || undefined;
      }
      
      const result = await updateAgency(currentAgency._id, {
        domains: domainsUpdate
      });
      
      if (result.success) {
        toast.success('Domain settings updated successfully');
        // The updateAgency function in CRMContext already updates currentAgency
        // The useEffect will pick up the change and update domainData
        // No need to reload the page
      } else {
        toast.error(result.error || 'Failed to update domain settings');
      }
    } catch (error) {
      console.error('Domain update error:', error);
      toast.error('An error occurred while updating domains');
    } finally {
      setDomainLoading(false);
    }
  };

  // Handle domain verification
  const handleDomainVerify = async () => {
    if (!currentAgency) return;
    
    setVerifyingDomain(true);
    try {
      const response = await agencyAPI.verifyDomain(currentAgency._id);
      
      if (response.data.success && response.data.data.verified) {
        toast.success('Domain verified successfully!');
        // The updateAgency function in CRMContext already updates currentAgency
        // The useEffect will pick up the change and update domainData
        setExpandedSteps(prev => ({ ...prev, step4: true }));
      } else {
        toast.error(response.data.message || 'Domain verification failed');
      }
    } catch (error) {
      console.error('Domain verification error:', error);
      toast.error(error.response?.data?.message || 'Failed to verify domain');
    } finally {
      setVerifyingDomain(false);
    }
  };

  // Handle domain unlinking
  const handleUnlinkDomain = async () => {
    if (!currentAgency) return;
    
    setDomainLoading(true);
    try {
      // Clear the custom domain by explicitly setting it to empty string
      // This will trigger the backend to delete the entire domains structure if both are empty
      const result = await updateAgency(currentAgency._id, {
        domains: {
          customDomain: '' // Explicitly set to empty string to clear
        }
      });
      
      if (result.success) {
        toast.success('Domain unlinked successfully');
        setShowUnlinkConfirm(false);
        // Clear the domain data in local state
        setDomainData(prev => ({ ...prev, customDomain: '' }));
        // Reset expanded steps
        setExpandedSteps({
          step1: true,
          step2: true,
          step3: true,
          step4: false
        });
      } else {
        toast.error(result.error || 'Failed to unlink domain');
      }
    } catch (error) {
      console.error('Unlink domain error:', error);
      toast.error(error.response?.data?.message || 'An error occurred while unlinking domain');
    } finally {
      setDomainLoading(false);
    }
  };

  const tabs = [
    { id: 'current-agency', name: 'Current Agency', icon: BuildingOfficeIcon },
    { id: 'team-management', name: 'Team Management', icon: UserIcon },
    { id: 'agency-management', name: 'Agency Management', icon: BuildingOfficeIcon },
    { id: 'branding', name: 'Branding', icon: SwatchIcon },
    { id: 'domains', name: 'Domain Management', icon: GlobeAltIcon },
    { id: 'billing', name: 'Billing', icon: StarIcon },
  ];

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-foreground mb-2">Agency Settings</h1>
        <p className="text-muted-foreground">Manage your agency, team, branding, domains, and billing in one place.</p>
      </div>

      {/* Tabs */}
      <div className="mb-8">
                <div className="border-b border-border overflow-x-auto overflow-y-hidden scrollbar-thin">
          <nav className="-mb-px flex space-x-4 sm:space-x-8 min-w-max sm:min-w-0">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    flex items-center py-2 px-1 sm:px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap flex-shrink-0
                    ${activeTab === tab.id
                      ? 'border-transparent'
                      : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'
                    }
                  `}
                  style={activeTab === tab.id ? { 
                    borderBottomColor: branding.primaryColor,
                    color: branding.primaryColor
                  } : {}}
                >
                  <Icon className="h-4 w-4 sm:h-5 sm:w-5 mr-1.5 sm:mr-2 flex-shrink-0" />
                  <span>{tab.name}</span>
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="bg-card rounded-lg shadow-sm border border-border p-6">
        {/* Current Agency Tab */}
        {activeTab === 'current-agency' && currentAgency && (
          <div className="space-y-8">
            <div>
              <h2 className="text-xl font-semibold text-foreground mb-4">Current Agency</h2>
              <p className="text-muted-foreground mb-6">
                Information about your currently selected agency.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Agency Info */}
              <div className="space-y-6">
                <div className="flex items-center space-x-4">
                  <AgencyLogo src={currentAgency.logoUrl || branding.agencyLogo} name={currentAgency.name || branding.agencyName} size={64} rounded="lg" />
                  
                  <div>
                    <h3 className="text-lg font-semibold text-foreground">{currentAgency.name}</h3>
                    {currentAgency.motto && (
                      <p className="text-sm text-muted-foreground">{currentAgency.motto}</p>
                    )}
                  </div>
                </div>

                {currentAgency.description && (
                  <div>
                    <h4 className="text-sm font-medium text-foreground mb-2">Description</h4>
                    <p className="text-sm text-muted-foreground">{currentAgency.description}</p>
                  </div>
                )}

                <div className="grid grid-cols-1 gap-4">
                  {currentAgency.email && (
                    <div>
                      <h4 className="text-sm font-medium text-foreground">Email</h4>
                      <p className="text-sm text-muted-foreground">{currentAgency.email}</p>
                    </div>
                  )}
                  {currentAgency.phone && (
                    <div>
                      <h4 className="text-sm font-medium text-foreground">Phone</h4>
                      <p className="text-sm text-muted-foreground">{currentAgency.phone}</p>
                    </div>
                  )}
                  {currentAgency.website && (
                    <div>
                      <h4 className="text-sm font-medium text-foreground">Website</h4>
                      <a 
                        href={currentAgency.website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm hover:opacity-80"
                        style={{ color: branding.primaryColor }}
                      >
                        {currentAgency.website}
                      </a>
                    </div>
                  )}
                </div>
              </div>

              {/* Quick Actions */}
              <div className="space-y-4">
                <h4 className="text-sm font-medium text-foreground">Quick Actions</h4>
                <div className="space-y-3">
                  <button
                    onClick={() => setActiveTab('agency-management')}
                    className="w-full flex items-center justify-center px-4 py-2 border border-input focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground rounded-md text-sm font-medium text-foreground hover:bg-muted"
                  >
                    <PencilIcon className="h-4 w-4 mr-2" />
                    Edit Agency
                  </button>
                  <button
                    onClick={() => setActiveTab('branding')}
                    className="w-full flex items-center justify-center px-4 py-2 border border-input focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground rounded-md text-sm font-medium text-foreground hover:bg-muted"
                  >
                    <SwatchIcon className="h-4 w-4 mr-2" />
                    Customize Branding
                  </button>
                  <button
                    onClick={() => setActiveTab('billing')}
                    className="w-full flex items-center justify-center px-4 py-2 border border-input focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground rounded-md text-sm font-medium text-foreground hover:bg-muted"
                  >
                    <StarIcon className="h-4 w-4 mr-2" />
                    Open Billing
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Billing Tab - inline billing management */}
        {activeTab === 'billing' && currentAgency && (
          <div className="space-y-4">
            <AgencyBilling agencyId={currentAgency._id} />
          </div>
        )}

        {/* Team Management Tab */}
        {activeTab === 'team-management' && (
          <TeamManagement />
        )}

        {/* Agency Management Tab */}
        {activeTab === 'agency-management' && (
          <div className="space-y-8">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-xl font-semibold text-foreground mb-2">Agency Management</h2>
                <p className="text-muted-foreground">
                  Create and manage multiple agencies. Your current plan allows for {remainingSlots === 'unlimited' ? 'unlimited' : remainingSlots} more agencies.
                </p>
              </div>
              <button
                onClick={() => canCreate ? setShowCreateForm(true) : null}
                disabled={!canCreate}
                className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium ${
                  canCreate 
                    ? 'text-white hover:opacity-90' 
                    : 'text-muted-foreground bg-gray-300 cursor-not-allowed'
                }`}
                style={canCreate ? { backgroundColor: branding.primaryColor } : {}}
                title={!canCreate ? `Upgrade your plan to create more agencies. Current limit: ${agencies.length} agencies` : 'Create a new agency'}
              >
                {canCreate ? (
                  <PlusIcon className="h-4 w-4 mr-2" />
                ) : (
                  <LockClosedIcon className="h-4 w-4 mr-2" />
                )}
                Create Agency
              </button>
            </div>

            {/* Agency Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-muted rounded-lg p-6">
                <div className="flex items-center">
                  <BuildingOfficeIcon className="h-8 w-8" style={{ color: branding.primaryColor }} />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-muted-foreground">Total Agencies</p>
                    <p className="text-2xl font-bold text-foreground">{agencies.length}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-muted rounded-lg p-6">
                <div className="flex items-center">
                  <StarIcon className="h-8 w-8" style={{ color: branding.accentColor }} />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-muted-foreground">Current Plan</p>
                    <p className="text-2xl font-bold text-foreground capitalize">
                      {(() => {
                        // Show the highest plan across all agencies (Enterprise if any agency is Enterprise)
                        const hasEnterprise = agencies?.some(agency => {
                          const plan = (agency?.planTier || agency?.subscription?.plan || '').toString().toLowerCase();
                          return plan === 'enterprise';
                        });
                        
                        if (hasEnterprise) {
                          return 'Enterprise';
                        }
                        
                        // Otherwise show current agency's plan
                        const plan = (currentAgency?.planTier || currentAgency?.subscription?.plan || '').toString().toLowerCase();
                        if (!plan) return 'trial';
                        return plan.charAt(0).toUpperCase() + plan.slice(1);
                      })()}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className={`rounded-lg p-6 ${canCreate ? 'bg-muted' : 'bg-red-50'}`}>
                <div className="flex items-center">
                  {canCreate ? (
                    <CheckCircleIcon className="h-8 w-8" style={{ color: branding.secondaryColor }} />
                  ) : (
                    <LockClosedIcon className="h-8 w-8" style={{ color: branding.accentColor }} />
                  )}
                  <div className="ml-4">
                    <p className="text-sm font-medium text-muted-foreground">Can Create More</p>
                    <p className={`text-2xl font-bold ${canCreate ? 'text-foreground' : 'text-red-600'}`}>
                      {canCreate ? 'Yes' : 'No'}
                    </p>
                    {!canCreate && (
                      <p className="text-xs mt-1" style={{ color: branding.accentColor }}>
                        Upgrade to create more
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Create/Edit Form */}
            {(showCreateForm || editingAgency) && (
              <div className="bg-muted rounded-lg p-6">
                <h3 className="text-lg font-semibold text-foreground mb-6">
                  {editingAgency ? 'Edit Agency' : 'Create New Agency'}
                </h3>
                
                <form onSubmit={editingAgency ? handleUpdateAgency : handleCreateAgency} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Agency Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 border border-input bg-background text-foreground rounded-md focus:outline-none focus:ring-2  focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                        style={{ '--tw-ring-color': branding.primaryColor }}
                        placeholder="Enter agency name"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Motto
                      </label>
                      <input
                        type="text"
                        name="motto"
                        value={formData.motto}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-input bg-background text-foreground rounded-md focus:outline-none focus:ring-2  focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                        style={{ '--tw-ring-color': branding.primaryColor }}
                        placeholder="Enter agency motto"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Description
                    </label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      rows={3}
                      className="w-full px-3 py-2 border border-input bg-background text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-ring  focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                      placeholder="Enter agency description"
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Email
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-input bg-background text-foreground rounded-md focus:outline-none focus:ring-2  focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                        style={{ '--tw-ring-color': branding.primaryColor }}
                        placeholder="contact@agency.com"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Phone
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-input bg-background text-foreground rounded-md focus:outline-none focus:ring-2  focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                        style={{ '--tw-ring-color': branding.primaryColor }}
                        placeholder="+34 123 456 789"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Address
                    </label>
                    <textarea
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      rows={2}
                      className="w-full px-3 py-2 border border-input bg-background text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-ring  focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                      placeholder="Enter agency address"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Website
                    </label>
                    <input
                      type="url"
                      name="website"
                      value={formData.website}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-input bg-background text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-ring  focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                      placeholder="https://www.agency.com"
                    />
                  </div>
                  
                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => {
                        setShowCreateForm(false);
                        setEditingAgency(null);
                        setFormData({
                          name: '',
                          description: '',
                          motto: '',
                          email: '',
                          phone: '',
                          address: '',
                          website: '',
                          logoUrl: ''
                        });
                      }}
                      className="px-4 py-2 border border-input focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground rounded-md text-sm font-medium text-foreground hover:bg-muted"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="px-4 py-2 text-white rounded-md text-sm font-medium hover:opacity-90 disabled:opacity-50"
                      style={{ backgroundColor: branding.primaryColor }}
                    >
                      {loading ? 'Saving...' : (editingAgency ? 'Update Agency' : 'Create Agency')}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Agencies List */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-foreground">Your Agencies</h3>
              <div className="space-y-4">
                {agencies.map((agency) => (
                  <div key={agency._id} className="bg-card border border-border rounded-lg p-6 hover:shadow-sm">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        {agency.logoUrl ? (
                          <img
                            src={agency.logoUrl}
                            alt={agency.name}
                            className="h-12 w-12 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="h-12 w-12 rounded-lg bg-muted flex items-center justify-center">
                            <BuildingOfficeIcon className="h-6 w-6 text-muted-foreground" />
                          </div>
                        )}
                        
                        <div>
                          <div className="flex items-center space-x-2">
                            <h4 className="text-lg font-semibold text-foreground">{agency.name}</h4>
                            {currentAgency?._id === agency._id && (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-white" style={{ backgroundColor: branding.secondaryColor }}>
                                Current
                              </span>
                            )}
                            {agency.isOwner ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                Owner
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                Member
                              </span>
                            )}
                          </div>
                          {agency.motto && (
                            <p className="text-sm text-muted-foreground">{agency.motto}</p>
                          )}
                          {agency.description && (
                            <p className="text-sm text-muted-foreground mt-1">{agency.description}</p>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        {currentAgency?._id !== agency._id && (
                          <button
                            onClick={() => handleSwitchAgency(agency._id)}
                            className="hover:opacity-80 text-sm font-medium"
                            style={{ color: branding.primaryColor }}
                          >
                            Switch to this agency
                          </button>
                        )}
                        
                        <button
                          onClick={() => handleEditAgency(agency)}
                          className="text-muted-foreground hover:text-muted-foreground"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </button>
                        
                        {agencies.length > 1 && agency.isOwner && (
                          <button
                            onClick={() => handleDeleteAgency(agency)}
                            className="hover:opacity-80"
                            style={{ color: branding.accentColor }}
                            title="Delete agency (owners only)"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        )}
                        {agencies.length > 1 && !agency.isOwner && (
                          <span className="text-muted-foreground text-xs" title="Only owners can delete agencies">
                            <LockClosedIcon className="h-5 w-5" />
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Upgrade Prompt */}
            {!canCreate && (
              <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <ExclamationTriangleIcon className="h-6 w-6 text-yellow-500" />
                    <div className="ml-3">
                      <h3 className="text-lg font-semibold text-yellow-800">
                        Agency Limit Reached
                      </h3>
                      <p className="text-sm text-yellow-700 mt-1">
                        You've reached the maximum number of agencies for your {user?.subscription?.tier || 'Free'} plan ({agencies.length} agencies). 
                        Upgrade to create more agencies and unlock additional features.
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      // This could navigate to a billing/upgrade page
                      toast.success('Redirecting to upgrade page...');
                    }}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                  >
                    <StarIcon className="h-4 w-4 mr-2" />
                    Upgrade Plan
                  </button>
                </div>
              </div>
            )}
          </div>
        )}


        {/* Branding Tab */}
        {activeTab === 'branding' && (
          <div className="space-y-8">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-xl font-semibold text-foreground mb-4">Brand Colors</h2>
                <p className="text-muted-foreground">
                  Choose colors that represent your brand. These will be applied throughout the CRM interface.
                </p>
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={handleResetBranding}
                  className="inline-flex items-center px-4 py-2 border border-input focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground rounded-md text-sm font-medium text-foreground hover:bg-muted"
                >
                  <ArrowPathIcon className="h-4 w-4 mr-2" />
                  Reset
                </button>
                <button
                  onClick={handleSaveBranding}
                  disabled={!hasChanges || brandingLoading}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                >
                  <CheckIcon className="h-4 w-4 mr-2" />
                  {brandingLoading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <ColorPicker
                label="Primary Color"
                description="Main brand color for buttons, links, and highlights"
                value={localBranding.primaryColor}
                onChange={(color) => handleColorChange('primaryColor', color)}
              />

              <ColorPicker
                label="Secondary Color"
                description="Supporting color for accents and secondary elements"
                value={localBranding.secondaryColor}
                onChange={(color) => handleColorChange('secondaryColor', color)}
              />

              <ColorPicker
                label="Accent Color"
                description="Color for alerts, notifications, and special elements"
                value={localBranding.accentColor}
                onChange={(color) => handleColorChange('accentColor', color)}
              />

              <ColorPicker
                label="Text Color"
                description="Primary text color for headings and important text"
                value={localBranding.textColor}
                onChange={(color) => handleColorChange('textColor', color)}
              />
            </div>

            {/* Agency Images */}
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-foreground">Agency Images</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Agency Logo
                  </label>
                  <ImageUpload
                    value={localBranding.agencyLogo}
                    onChange={(image) => handleImageChange('agencyLogo', image)}
                    aspectRatio="16:9"
                    maxSize={5 * 1024 * 1024} // 5MB
                    placeholder="Upload agency logo"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Agency Cover Image
                </label>
                <ImageUpload
                  value={localBranding.agencyCoverImage}
                  onChange={(image) => handleImageChange('agencyCoverImage', image)}
                  aspectRatio="16:9"
                  maxSize={5 * 1024 * 1024} // 5MB
                  placeholder="Upload agency cover image"
                />
              </div>
            </div>
          </div>
        )}

        {/* Domain Management Tab */}
        {activeTab === 'domains' && currentAgency && (
          <div className="space-y-8">
            <div>
              <h2 className="text-xl font-semibold text-foreground mb-4">Domain Management</h2>
              <p className="text-muted-foreground mb-6">
                Configure custom domains for your branded login portal. Clients can access your portal using your domain instead of the default CRM domain.
              </p>
            </div>

            {/* Subdomain Configuration */}
            <div className="space-y-6">
              <div className="border border-border rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <LinkIcon className="h-5 w-5 text-muted-foreground mr-2" />
                    <h3 className="text-lg font-semibold text-foreground">Subdomain</h3>
                  </div>
                  {currentAgency.domains?.subdomain && (
                    <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 rounded">
                      Active
                    </span>
                  )}
                </div>
                
                <p className="text-sm text-muted-foreground mb-4">
                  Set up a subdomain for your branded login portal. Your clients can access the portal at:
                  <code className="ml-1 px-2 py-1 bg-muted rounded text-xs font-mono">
                    {domainData.subdomain || 'your-subdomain'}.livelovespain.com/login
                  </code>
                </p>

                <div className="space-y-4">
                  <div>
                    <label htmlFor="subdomain" className="block text-sm font-medium text-foreground mb-2">
                      Subdomain
                    </label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="text"
                        id="subdomain"
                        value={domainData.subdomain}
                        onChange={(e) => {
                          // Allow user to type freely, only filter invalid characters
                          const value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
                          setDomainData(prev => ({ ...prev, subdomain: value }));
                        }}
                        placeholder="your-agency-name"
                        className="flex-1 px-3 py-2 border border-input bg-background text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
                        pattern="[a-z0-9-]+"
                        minLength={3}
                        maxLength={63}
                      />
                      <span className="text-sm text-muted-foreground whitespace-nowrap">.livelovespain.com</span>
                    </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        3-63 characters, alphanumeric and hyphens only
                      </p>
                    </div>

                    {/* Save Button for Subdomain */}
                    <div className="flex justify-end mt-4 pt-3 border-t border-border">
                      <button
                        onClick={handleDomainUpdate}
                        disabled={domainLoading || 
                          (domainData.subdomain.trim() || '') === (currentAgency.domains?.subdomain || '')}
                        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {domainLoading ? (
                          <>
                            <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                            Saving...
                          </>
                        ) : (
                          <>
                            <CheckIcon className="h-4 w-4 mr-2" />
                            {currentAgency.domains?.subdomain ? 'Update Subdomain' : 'Save Subdomain'}
                          </>
                        )}
                      </button>
                    </div>

                  {currentAgency.domains?.subdomain && (
                    <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                      <div className="flex items-start">
                        <InformationCircleIcon className="h-5 w-5 text-blue-600 dark:text-blue-400 mr-2 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-sm text-blue-900 dark:text-blue-200 font-medium mb-1">
                            Subdomain is active
                          </p>
                          <p className="text-xs text-blue-700 dark:text-blue-300">
                            Your login portal is available at:{' '}
                            <a
                              href={`https://${currentAgency.domains.subdomain}.livelovespain.com/login`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="font-mono underline"
                            >
                              https://{currentAgency.domains.subdomain}.livelovespain.com/login
                            </a>
                          </p>
                          <p className="text-xs text-blue-700 dark:text-blue-300 mt-2">
                            Note: DNS configuration may be required. Contact support if the subdomain doesn't work immediately.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Custom Domain Configuration (Professional & Enterprise) */}
              <div className="border border-border rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <GlobeAltIcon className="h-5 w-5 text-muted-foreground mr-2" />
                    <h3 className="text-lg font-semibold text-foreground">Custom Domain</h3>
                    {!isCustomDomainAvailable() && (
                      <span className="ml-2 px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200 rounded">
                        Professional+
                      </span>
                    )}
                  </div>
                  {currentAgency.domains?.customDomain && (
                    <div className="flex items-center space-x-2">
                      {currentAgency.domains.domainStatus === 'verified' && (
                        <CheckCircleIcon className="h-5 w-5 text-green-600" />
                      )}
                      {currentAgency.domains.domainStatus === 'pending' && (
                        <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600" />
                      )}
                      <span className={`px-2 py-1 text-xs font-medium rounded ${
                        currentAgency.domains.domainStatus === 'verified' 
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                      }`}>
                        {currentAgency.domains.domainStatus === 'verified' ? 'Verified' : 'Pending Verification'}
                      </span>
                    </div>
                  )}
                </div>

                {!isCustomDomainAvailable() ? (
                  <div className="bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6">
                    <div className="flex items-start">
                      <LockClosedIcon className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mr-2 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <h4 className="text-sm font-semibold text-yellow-900 dark:text-yellow-200 mb-2">
                          Custom Domain (Professional & Enterprise Feature)
                        </h4>
                        <p className="text-sm text-yellow-700 dark:text-yellow-300 mb-4">
                          Custom domains allow you to use your own domain name for the login portal (e.g., portal.yourcompany.com).
                          This feature is available for Professional and Enterprise tier subscribers.
                        </p>
                        {(() => {
                          const currentTier = subscription?.tier || 'FREE';
                          const isProfessional = currentTier === 'PROFESSIONAL';
                          const upgradeText = isProfessional 
                            ? 'Upgrade to Enterprise plan for advanced features' 
                            : 'Upgrade to Professional plan to unlock custom domains';
                          const upgradePlan = isProfessional ? 'Enterprise' : 'Professional';
                          
                          return (
                            <button
                              onClick={() => {
                                toast.info(upgradeText);
                              }}
                              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700"
                            >
                              <StarIcon className="h-4 w-4 mr-2" />
                              Upgrade to {upgradePlan}
                            </button>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Step 1: Enter Domain */}
                    <div className="relative">
                      {/* Timeline line */}
                      {currentAgency.domains?.customDomain && (
                        <div className="absolute left-6 top-12 bottom-0 w-0.5 bg-border"></div>
                      )}
                      
                      <div className="flex items-start space-x-4">
                        {/* Step number */}
                        <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center font-semibold text-sm ${
                          domainData.customDomain 
                            ? 'bg-blue-600 text-white' 
                            : 'bg-muted text-muted-foreground'
                        }`}>
                          1
                        </div>
                        
                        {/* Step content */}
                        <div className="flex-1">
                          <div className="bg-card border border-border rounded-lg p-4">
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="text-base font-semibold text-foreground">
                                Enter Your Custom Domain
                              </h4>
                              {currentAgency.domains?.customDomain && (
                                <button
                                  type="button"
                                  onClick={() => setExpandedSteps(prev => ({ ...prev, step1: !prev.step1 }))}
                                  className="text-muted-foreground hover:text-foreground"
                                >
                                  {expandedSteps.step1 ? (
                                    <ChevronUpIcon className="h-5 w-5" />
                                  ) : (
                                    <ChevronDownIcon className="h-5 w-5" />
                                  )}
                                </button>
                              )}
                            </div>
                            
                            {(expandedSteps.step1 || !currentAgency.domains?.customDomain) && (
                              <div className="space-y-3">
                                <p className="text-sm text-muted-foreground">
                                  Configure your own domain for a fully white-labeled experience. Your clients can access the portal at:
                                  <code className="ml-1 px-2 py-1 bg-muted rounded text-xs font-mono">
                                    {domainData.customDomain || 'portal.yourdomain.com'}/login
                                  </code>
                                </p>
                                
                                <div>
                                  <label htmlFor="customDomain" className="block text-sm font-medium text-foreground mb-2">
                                    Custom Domain
                                  </label>
                                  <input
                                    type="text"
                                    id="customDomain"
                                    value={domainData.customDomain}
                                    onChange={(e) => {
                                      const value = e.target.value.toLowerCase();
                                      setDomainData(prev => ({ ...prev, customDomain: value }));
                                    }}
                                    placeholder="portal.yourcompany.com"
                                    className="w-full px-3 py-2 border border-input bg-background text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
                                  />
                                  <p className="text-xs text-muted-foreground mt-1">
                                    Enter your full domain name (e.g., portal.yourcompany.com)
                                  </p>
                                </div>

                                {currentAgency.domains?.customDomain && currentAgency.domains.domainStatus === 'verified' && (
                                  <div className="flex items-center space-x-2 mt-3 p-2 bg-green-50 dark:bg-green-950/20 rounded">
                                    <CheckCircleIcon className="h-5 w-5 text-green-600" />
                                    <span className="text-sm text-green-700 dark:text-green-300">
                                      Domain configured: <code className="font-mono">{currentAgency.domains.customDomain}</code>
                                    </span>
                                  </div>
                                )}

                                {/* Action Buttons for Step 1 */}
                                <div className="flex justify-end space-x-3 mt-4 pt-3 border-t border-border">
                                  {currentAgency.domains?.customDomain && (
                                    <button
                                      onClick={() => setShowUnlinkConfirm(true)}
                                      disabled={domainLoading}
                                      className="inline-flex items-center px-4 py-2 border border-red-300 dark:border-red-700 rounded-md shadow-sm text-sm font-medium text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-950/20 hover:bg-red-100 dark:hover:bg-red-900/30 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                      <LinkIcon className="h-4 w-4 mr-2 rotate-45" />
                                      Unlink Domain
                                    </button>
                                  )}
                                  <button
                                    onClick={handleDomainUpdate}
                                    disabled={domainLoading || 
                                      (domainData.customDomain.trim() || '') === (currentAgency.domains?.customDomain || '')}
                                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    {domainLoading ? (
                                      <>
                                        <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                                        Saving...
                                      </>
                                    ) : (
                                      <>
                                        <CheckIcon className="h-4 w-4 mr-2" />
                                        {currentAgency.domains?.customDomain ? 'Update Domain' : 'Save Domain'}
                                      </>
                                    )}
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Steps 2-4 only show if domain is configured */}
                    {currentAgency.domains?.customDomain && (
                      <div className="space-y-6">
                        {/* Step 2: CNAME Record */}
                        <div className="relative">
                          {/* Timeline line */}
                          <div className="absolute left-6 top-12 bottom-0 w-0.5 bg-border"></div>
                          
                          <div className="flex items-start space-x-4">
                            {/* Step number */}
                            <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center font-semibold text-sm ${
                              currentAgency.domains.dnsRecords?.cname 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-muted text-muted-foreground'
                            }`}>
                              2
                            </div>
                            
                            {/* Step content */}
                            <div className="flex-1">
                              <div className="bg-card border border-border rounded-lg p-4">
                                <div className="flex items-center justify-between mb-2">
                                  <h4 className="text-base font-semibold text-foreground">
                                    Add CNAME Record (Required)
                                  </h4>
                                  <button
                                    type="button"
                                    onClick={() => setExpandedSteps(prev => ({ ...prev, step2: !prev.step2 }))}
                                    className="text-muted-foreground hover:text-foreground"
                                  >
                                    {expandedSteps.step2 ? (
                                      <ChevronUpIcon className="h-5 w-5" />
                                    ) : (
                                      <ChevronDownIcon className="h-5 w-5" />
                                    )}
                                  </button>
                                </div>
                                
                                {expandedSteps.step2 && (
                                  <div className="space-y-3">
                                    <p className="text-sm text-muted-foreground">
                                      Point your custom domain to our platform by adding a CNAME record:
                                    </p>
                                    
                                    <div className="bg-muted rounded-lg p-4 font-mono text-sm space-y-2">
                                      <div className="flex items-center justify-between py-1">
                                        <span className="text-muted-foreground">Type:</span>
                                        <span className="text-foreground font-semibold">CNAME</span>
                                      </div>
                                      <div className="flex items-center justify-between py-1 border-t border-border">
                                        <span className="text-muted-foreground">Name/Host:</span>
                                        <span className="text-foreground font-semibold">
                                          {(() => {
                                            const domainParts = currentAgency.domains.customDomain.split('.');
                                            if (domainParts.length > 2) {
                                              return domainParts[0];
                                            }
                                            return '@';
                                          })()}
                                        </span>
                                      </div>
                                      <div className="flex items-center justify-between py-1 border-t border-border">
                                        <span className="text-muted-foreground">Target/Value:</span>
                                        <span className="text-foreground font-semibold break-all text-right">
                                          {currentAgency.domains.dnsRecords?.cname || 
                                           (() => {
                                             const hostname = window.location.hostname;
                                             if (hostname === 'localhost' || hostname.includes('127.0.0.1')) {
                                               return 'cname.livelovespain.com';
                                             }
                                             const parts = hostname.split('.');
                                             if (parts.length >= 2) {
                                               return `cname.${parts.slice(-2).join('.')}`;
                                             }
                                             return 'cname.livelovespain.com';
                                           })()}
                                        </span>
                                      </div>
                                      <div className="flex items-center justify-between py-1 border-t border-border">
                                        <span className="text-muted-foreground">TTL:</span>
                                        <span className="text-foreground">3600 (or default)</span>
                                      </div>
                                    </div>
                                    
                                    <div className="bg-blue-50 dark:bg-blue-950/20 rounded-lg p-3 mt-3">
                                      <p className="text-xs font-medium text-blue-900 dark:text-blue-200 mb-2">
                                        Important Notes:
                                      </p>
                                      <ul className="text-xs text-blue-700 dark:text-blue-300 space-y-1 ml-4 list-disc">
                                        <li>If your domain provider doesn't support CNAME at the root, use a subdomain like portal.yourdomain.com instead</li>
                                        <li>The CNAME target may differ based on your hosting setup</li>
                                        <li>Some DNS providers require @ or root domain for apex domains</li>
                                      </ul>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Step 3: TXT Verification Record */}
                        <div className="relative">
                          {/* Timeline line */}
                          <div className="absolute left-6 top-12 bottom-0 w-0.5 bg-border"></div>
                          
                          <div className="flex items-start space-x-4">
                            {/* Step number */}
                            <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center font-semibold text-sm ${
                              currentAgency.domains.dnsRecords?.txt 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-muted text-muted-foreground'
                            }`}>
                              3
                            </div>
                            
                            {/* Step content */}
                            <div className="flex-1">
                              <div className="bg-card border border-border rounded-lg p-4">
                                <div className="flex items-center justify-between mb-2">
                                  <h4 className="text-base font-semibold text-foreground">
                                    Add TXT Verification Record (Required)
                                  </h4>
                                  <button
                                    type="button"
                                    onClick={() => setExpandedSteps(prev => ({ ...prev, step3: !prev.step3 }))}
                                    className="text-muted-foreground hover:text-foreground"
                                  >
                                    {expandedSteps.step3 ? (
                                      <ChevronUpIcon className="h-5 w-5" />
                                    ) : (
                                      <ChevronDownIcon className="h-5 w-5" />
                                    )}
                                  </button>
                                </div>
                                
                                {expandedSteps.step3 && (
                                  <div className="space-y-3">
                                    <p className="text-sm text-muted-foreground">
                                      Add this TXT record to verify domain ownership:
                                    </p>
                                    
                                    <div className="bg-muted rounded-lg p-4 font-mono text-sm space-y-2">
                                      <div className="flex items-center justify-between py-1">
                                        <span className="text-muted-foreground">Type:</span>
                                        <span className="text-foreground font-semibold">TXT</span>
                                      </div>
                                      <div className="flex items-center justify-between py-1 border-t border-border">
                                        <span className="text-muted-foreground">Name/Host:</span>
                                        <span className="text-foreground font-semibold">
                                          {(() => {
                                            const domainParts = currentAgency.domains.customDomain.split('.');
                                            if (domainParts.length > 2) {
                                              return `_verify.${domainParts[0]}`;
                                            }
                                            return '_verify';
                                          })()}
                                        </span>
                                      </div>
                                      <div className="flex items-start justify-between py-1 border-t border-border">
                                        <span className="text-muted-foreground">Value:</span>
                                        <span className="text-foreground font-semibold break-all text-right max-w-[70%]">
                                          {currentAgency.domains.dnsRecords?.txt || 'Generating...'}
                                        </span>
                                      </div>
                                      <div className="flex items-center justify-between py-1 border-t border-border">
                                        <span className="text-muted-foreground">TTL:</span>
                                        <span className="text-foreground">3600 (or default)</span>
                                      </div>
                                    </div>
                                    
                                    <div className="bg-yellow-50 dark:bg-yellow-950/20 rounded-lg p-3 mt-3">
                                      <p className="text-xs text-yellow-700 dark:text-yellow-300">
                                        <strong>Example:</strong> If your domain is <code className="px-1 py-0.5 bg-yellow-100 dark:bg-yellow-900 rounded">portal.yourcompany.com</code>, 
                                        add a TXT record with name <code className="px-1 py-0.5 bg-yellow-100 dark:bg-yellow-900 rounded">_verify.portal</code>
                                      </p>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Step 4: Verify & Activate */}
                        <div className="relative">
                          <div className="flex items-start space-x-4">
                            {/* Step number */}
                            <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center font-semibold text-sm ${
                              currentAgency.domains.domainStatus === 'verified' 
                                ? 'bg-green-600 text-white' 
                                : 'bg-muted text-muted-foreground'
                            }`}>
                              {currentAgency.domains.domainStatus === 'verified' ? (
                                <CheckCircleIcon className="h-6 w-6" />
                              ) : (
                                '4'
                              )}
                            </div>
                            
                            {/* Step content */}
                            <div className="flex-1">
                              <div className="bg-card border border-border rounded-lg p-4">
                                <div className="flex items-center justify-between mb-2">
                                  <h4 className="text-base font-semibold text-foreground">
                                    {currentAgency.domains.domainStatus === 'verified' 
                                      ? 'Domain Verified & Active' 
                                      : 'Verify & Activate Domain'}
                                  </h4>
                                  {currentAgency.domains.domainStatus === 'verified' && (
                                    <button
                                      type="button"
                                      onClick={() => setExpandedSteps(prev => ({ ...prev, step4: !prev.step4 }))}
                                      className="text-muted-foreground hover:text-foreground"
                                    >
                                      {expandedSteps.step4 ? (
                                        <ChevronUpIcon className="h-5 w-5" />
                                      ) : (
                                        <ChevronDownIcon className="h-5 w-5" />
                                      )}
                                    </button>
                                  )}
                                </div>
                                
                                {currentAgency.domains.domainStatus === 'verified' ? (
                                  (expandedSteps.step4 || !expandedSteps.step4) && (
                                    <div className="space-y-3">
                                      <div className="bg-green-50 dark:bg-green-950/20 rounded-lg p-3">
                                        <p className="text-sm text-green-700 dark:text-green-300 mb-2">
                                          Your custom domain is verified and active. Your login portal is available at:
                                        </p>
                                        <a
                                          href={`https://${currentAgency.domains.customDomain}/login`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-sm font-mono text-green-600 dark:text-green-400 underline"
                                        >
                                          https://{currentAgency.domains.customDomain}/login
                                        </a>
                                        {currentAgency.domains.dnsRecords?.verifiedAt && (
                                          <p className="text-xs text-green-600 dark:text-green-400 mt-2">
                                            Verified on: {new Date(currentAgency.domains.dnsRecords.verifiedAt).toLocaleDateString()}
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  )
                                ) : (
                                  <div className="space-y-3">
                                    <p className="text-sm text-muted-foreground">
                                      After adding both DNS records:
                                    </p>
                                    <ul className="text-sm text-muted-foreground space-y-1 ml-4 list-disc">
                                      <li>Wait 5-60 minutes for DNS propagation</li>
                                      <li>Click "Verify Domain" below to check DNS records</li>
                                      <li>SSL certificate will be automatically issued via Let's Encrypt</li>
                                      <li>Your domain will be active once verified</li>
                                    </ul>
                                    <div className="bg-yellow-50 dark:bg-yellow-950/20 rounded-lg p-3 mt-3">
                                      <p className="text-xs text-yellow-700 dark:text-yellow-300">
                                        <strong>Tip:</strong> DNS changes can take up to 48 hours to fully propagate, but typically work within minutes.
                                      </p>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Verify Button & Unlink Button */}
                        {currentAgency.domains.domainStatus !== 'verified' && (
                          <div className="flex justify-end pt-4">
                            <button
                              onClick={handleDomainVerify}
                              disabled={verifyingDomain}
                              className="inline-flex items-center justify-center px-6 py-2.5 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {verifyingDomain ? (
                                <>
                                  <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                                  Verifying...
                                </>
                              ) : (
                                <>
                                  <CheckCircleIcon className="h-4 w-4 mr-2" />
                                  Verify Domain
                                </>
                              )}
                            </button>
                          </div>
                        )}

                      </div>
                    )}

                  </div>
                )}
              </div>

            </div>
          </div>
        )}
      </div>

      <ConfirmationDialog />
      
      {/* Unlink Domain Confirmation Modal */}
      {showUnlinkConfirm && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50">
          <div className="bg-card border border-border rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600 mr-3" />
                <h3 className="text-lg font-semibold text-foreground">
                  Unlink Custom Domain
                </h3>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                Are you sure you want to unlink the custom domain{' '}
                <code className="px-1 py-0.5 bg-muted rounded text-xs font-mono">
                  {currentAgency.domains?.customDomain}
                </code>? This will remove all domain configuration and DNS settings.
              </p>
              <p className="text-xs text-muted-foreground mb-6">
                You can always reconfigure the domain later if needed.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowUnlinkConfirm(false)}
                  disabled={domainLoading}
                  className="px-4 py-2 border border-border rounded-md text-sm font-medium text-foreground bg-background hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUnlinkDomain}
                  disabled={domainLoading}
                  className="px-4 py-2 border border-red-300 dark:border-red-700 rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {domainLoading ? (
                    <>
                      <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin inline" />
                      Unlinking...
                    </>
                  ) : (
                    'Unlink Domain'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgencySettings;
